import React, { useEffect, useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Choice as Checkbox, TextField } from '@cmsgov/design-system';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Components

import DropdownAdapter from './DropdownAdapter';

// Utils

import { formatDate, getDateTimeInMillisecond, isDateSameOrAfter, isDateValid } from '@utils/dates/date.utils';

// Constants

import {
    CORRESPONDING_DATES,
    MANDATORY_INPUT_NAMES_FOR_DATE_SEARCH,
    SEARCH_INPUT_DEFAULTS,
    SEARCH_INPUT_NAMES,
    SEARCH_INPUT_TYPES,
} from '@constants/ticketMgmt/manage-assignees/searchTypes';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';

const UniversalSearchInput = props => {
    const {
        handleInputChange,
        label,
        labelClass,
        name,
        id,
        placeholder,
        options = [],
        refresher,
        searchValues,
        srLabelText,
        stretch,
        type,
        validation: maxRange,
    } = props;

    const defaultValue = SEARCH_INPUT_DEFAULTS[name];

    const [value, updateValue] = useState(searchValues[name]);

    useEffect(() => {
        if (refresher) {
            updateValue(defaultValue);
        }
    }, [refresher]);

    const allMandatoryInputsForDateSearchEmpty = useMemo(
        () => MANDATORY_INPUT_NAMES_FOR_DATE_SEARCH.every(inputName => !searchValues[inputName]),
        [searchValues],
    );

    useEffect(() => {
        if (
            allMandatoryInputsForDateSearchEmpty &&
            (name === SEARCH_INPUT_NAMES.AFTER_DATE || name === SEARCH_INPUT_NAMES.BEFORE_DATE)
        ) {
            updateValue(SEARCH_INPUT_DEFAULTS[name]);
        }
    }, [allMandatoryInputsForDateSearchEmpty]);

    const intlApi = useIntl();

    const handleCheckboxChange = () => {
        handleInputChange({ value: !value, name });
        updateValue(!value);
    };

    const handleRegularInputChange = e => {
        const { value } = e.target;

        if (value.length > maxRange) return;
        handleInputChange({ value, name });
        updateValue(value);
    };

    const handleDropdownChange = e => {
        const { value } = e.target;

        let newValue = value;

        switch (value) {
            case 'true':
                newValue = true;
                break;
            case 'false':
                newValue = false;
                break;
            case 'null':
                newValue = null;
                break;
        }

        handleInputChange({ value: newValue, name });
        updateValue(value);
    };

    const verifyDateValue = newDate => {
        switch (true) {
            case !isDateValid(newDate, MM_SLASH_DD_SLASH_YYYY):
                return false;
            case !isDateSameOrAfter(new Date(), newDate):
                return false;
            case !searchValues[CORRESPONDING_DATES[name]]:
                return true;
            case name === SEARCH_INPUT_NAMES.BEFORE_DATE &&
                isDateSameOrAfter(newDate, searchValues[CORRESPONDING_DATES[name]]):
            case name === SEARCH_INPUT_NAMES.AFTER_DATE &&
                isDateSameOrAfter(searchValues[CORRESPONDING_DATES[name]], newDate):
                return true;
            default:
                return false;
        }
    };

    const setDateToDefault = () => {
        updateValue(defaultValue);
        handleInputChange({ value: defaultValue, name });
    };

    const handleDateChange = (...args) => {
        if (allMandatoryInputsForDateSearchEmpty) return;
        const [date] = args;
        const newDate = formatDate(date, MM_SLASH_DD_SLASH_YYYY);
        if (verifyDateValue(newDate)) {
            updateValue(newDate);
            handleInputChange({ value: newDate, name });
        } else {
            setDateToDefault();
        }
    };

    const handleRegularInputDatepickerBlur = () => {
        if (allMandatoryInputsForDateSearchEmpty) return;
        const regex = /\d{2}\/\d{2}\/\d{4}/;
        if (!regex.test(value) || !verifyDateValue(value)) {
            setDateToDefault();
        }
    };

    const CalendarIcon = React.forwardRef(({ children, onClick }, ref) => {
        return (
            <span ref={ref} onClick={onClick}>
                {children}
            </span>
        );
    });

    CalendarIcon.displayName = 'CalendarIcon';

    const effectiveLabel = srLabelText || label;
    const labelClassName = srLabelText ? `sr-only ${labelClass}` : labelClass;
    
    useEffect(() => {
        if (effectiveLabel === undefined || effectiveLabel === '') {
            document.getElementById(`${id}-label`)?.remove();
        }
    }, [id, effectiveLabel]);

    switch (type) {
        case SEARCH_INPUT_TYPES.DROPDOWN:
            return (
                <DropdownAdapter
                    className="search-dropdown display--inline  desktop:grid-col-${stretch ? 12 : 6}"
                    labelClassName={labelClassName}
                    label={effectiveLabel}
                    id={id}
                    name={name}
                    onChange={handleDropdownChange}
                    options={options}
                    value={value}
                />
            );
        case SEARCH_INPUT_TYPES.INPUT:
            return (
                <TextField
                    className="search-regular-input width-full"
                    labelClassName={labelClassName}
                    label={effectiveLabel}
                    id={id}
                    name={name}
                    onChange={handleRegularInputChange}
                    placeholder={intlApi.formatMessage({ id: placeholder })}
                    value={value}
                />
            );
        case SEARCH_INPUT_TYPES.CHECKBOX:
            return (
                <Checkbox
                    checked={value}
                    className="search-checkbox"
                    name={name}
                    id={id}
                    onChange={handleCheckboxChange}
                    value={1}
                >
                    <FormattedMessage id={label} />
                </Checkbox>
            );
        case SEARCH_INPUT_TYPES.DATEPICKER:
            return (
                <span className="search-datepicker display--inline desktop:grid-col-6 position-relative">
                    <TextField
                        className="search-regular-input width-full"
                        disabled={allMandatoryInputsForDateSearchEmpty}
                        labelClassName={labelClassName}
                        label={effectiveLabel}
                        id={id}
                        name={name}
                        onBlur={handleRegularInputDatepickerBlur}
                        onChange={handleRegularInputChange}
                        placeholder={intlApi.formatMessage({ id: placeholder })}
                        value={value || ''}
                    />
                    <DatePicker
                        customInput={
                            <CalendarIcon>
                                <FontAwesomeIcon
                                    icon="calendar-alt"
                                    className={allMandatoryInputsForDateSearchEmpty ? 'no-cursor' : 'pointer'}
                                />
                            </CalendarIcon>
                        }
                        disabled={allMandatoryInputsForDateSearchEmpty}
                        id={id}
                        onChange={handleDateChange}
                        selected={value ? getDateTimeInMillisecond(value) : ''}
                    />
                </span>
            );
        default:
            return null;
    }
};

export default UniversalSearchInput;
